import React, { createContext, useState, useContext, useEffect } from 'react';

const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

const translations = {
  en: {
    // Navbar
    home: 'Home',
    about: 'About',
    products: 'Products',
    faq: 'FAQ',
    contact: 'Contact',
    payments: 'Payments',
    refund: 'Refund Policy',
    orders: 'Orders',
    myProfile: 'My Profile',
    myOrders: 'My Orders',
    signOut: 'Sign out',
    viewCart: 'View cart',
    login: 'Login',
    
    // Home Page
    whyChooseUs: 'Why Choose SubifyHub?',
    trustedByUsers: 'Trusted by over 50,000 users worldwide. Here\'s what makes us different.',
    genuineProducts: '100% Genuine Products',
    genineDesc: 'All products are authentic and verified from official sources',
    securePayment: 'Secure Payment',
    secureDesc: 'Your payment information is protected with SSL encryption',
    instantDelivery: 'Instant Delivery',
    instantDesc: 'Receive your subscription credentials via email immediately',
    dedicatedSupport: '24/7 Dedicated Support',
    supportDesc: 'Our team is always ready to help you with any questions',
    bestPrice: 'Best Price Guarantee',
    priceDesc: 'We offer the most competitive prices in the market',
    moneyBack: 'Money Back Guarantee',
    moneyBackDesc: 'Not satisfied? Get a full refund within 30 days',
    
    // Product Detail
    addToCart: 'Add to Cart',
    monthly: 'Monthly',
    yearly: 'Yearly',
    billedMonthly: 'Billed monthly',
    billedYearly: 'Billed yearly',
    outOfStock: 'Out of Stock',
    inStock: 'in stock',
    featuresHighlights: 'Features & Highlights',
    howDoesItWork: 'How Does This Work?',
    instantDeliveryEmail: 'Instant Delivery via Email',
    whyShouldChoose: 'Why Should You Choose This Service?',
    advancedFeatures: 'Advanced Features',
    benefits: 'Benefits',
    professionalTools: 'Professional tools and resources',
    unlimitedAccess: 'Unlimited access to premium content',
    dedicatedSupportLine: '24/7 dedicated support',
    saveTime: 'Save time and increase productivity',
    allFeaturesUnlocked: 'All features unlocked and ready to use',
    plansAvailable: 'Monthly or lifetime plans available',
    paymentMethods: 'Payment Methods',
    needHelp: 'Need Help?',
    creditCard: 'Credit/Debit Card',
    paypal: 'Paypal Xoom.com',
    westernUnion: 'Western union',
    revolut: 'Revolut',
    binance: 'Binance',
    liveChat: 'Live Chat Support Available',
    hours: '10 AM - 11 PM (PKT)',
    customerReviews: 'Customer Reviews',
    description: 'Description',
    
    // Contact Page
    getInTouchWithUs: 'Get in Touch with Us',
    contactIntro: 'We are committed to providing the best support experience',
    contactAvailable: '24/7 Support Available',
    responseTime: 'Response time within 1 hour',
    email: 'Email',
    whatsapp: 'WhatsApp',
    telegram: 'Telegram',
    faqSection: 'Frequently Asked Questions',
    trustPrivacy: 'Trust & Privacy',
    contactUs: 'Contact Us',
    
    // FAQ Page
    frequentlyAskedQuestions: 'Frequently Asked Questions',
    
    // Footer
    premiumSubscriptions: 'Premium subscriptions at exceptional value. Genuine products, dedicated support, and complete peace of mind.',
    categories: 'Categories',
    quickLinks: 'Quick Links',
    support: 'Support',
    allRightsReserved: 'All Rights Reserved',
    securePayments: 'Secure Payments',
    sslEncrypted: 'SSL Encrypted',
  },
  fr: {
    // Navbar
    home: 'Accueil',
    about: 'À propos',
    products: 'Produits',
    faq: 'FAQ',
    contact: 'Contact',
    payments: 'Paiements',
    refund: 'Politique de Remboursement',
    orders: 'Commandes',
    myProfile: 'Mon Profil',
    myOrders: 'Mes Commandes',
    signOut: 'Déconnexion',
    viewCart: 'Voir le panier',
    login: 'Connexion',
    
    // Home Page
    whyChooseUs: 'Pourquoi Choisir SubifyHub?',
    trustedByUsers: 'Approuvé par plus de 50 000 utilisateurs dans le monde. Voici ce qui nous rend différents.',
    genuineProducts: '100% Produits Authentiques',
    genineDesc: 'Tous les produits sont authentiques et vérifiés auprès de sources officielles',
    securePayment: 'Paiement Sécurisé',
    secureDesc: 'Vos informations de paiement sont protégées par le chiffrement SSL',
    instantDelivery: 'Livraison Instantanée',
    instantDesc: 'Recevez vos identifiants d\'abonnement par e-mail immédiatement',
    dedicatedSupport: 'Support Dédié 24/7',
    supportDesc: 'Notre équipe est toujours prête à vous aider avec vos questions',
    bestPrice: 'Garantie du Meilleur Prix',
    priceDesc: 'Nous offrons les prix les plus compétitifs du marché',
    moneyBack: 'Garantie de Remboursement',
    moneyBackDesc: 'Non satisfait? Obtenez un remboursement complet dans les 30 jours',
    
    // Product Detail
    addToCart: 'Ajouter au Panier',
    monthly: 'Mensuel',
    yearly: 'Annuel',
    billedMonthly: 'Facturé mensuellement',
    billedYearly: 'Facturé annuellement',
    outOfStock: 'Rupture de Stock',
    inStock: 'en stock',
    featuresHighlights: 'Caractéristiques et Avantages',
    howDoesItWork: 'Comment Ça Marche?',
    instantDeliveryEmail: 'Livraison Instantanée par Email',
    whyShouldChoose: 'Pourquoi Devriez-Vous Choisir Ce Service?',
    advancedFeatures: 'Fonctionnalités Avancées',
    benefits: 'Avantages',
    professionalTools: 'Outils et ressources professionnels',
    unlimitedAccess: 'Accès illimité au contenu premium',
    dedicatedSupportLine: 'Support dédié 24/7',
    saveTime: 'Économiser du temps et augmenter la productivité',
    allFeaturesUnlocked: 'Tous les éléments débloqués et prêts à être utilisés',
    plansAvailable: 'Plans mensuels ou à vie disponibles',
    paymentMethods: 'Méthodes de Paiement',
    needHelp: 'Besoin d\'Aide?',
    creditCard: 'Carte de Crédit/Débit',
    paypal: 'Paypal Xoom.com',
    westernUnion: 'Western Union',
    revolut: 'Revolut',
    binance: 'Binance',
    liveChat: 'Support par Chat en Direct Disponible',
    hours: '10h - 23h (PKT)',
    customerReviews: 'Avis des Clients',
    description: 'Description',
    
    // Contact Page
    getInTouchWithUs: 'Mettez-Vous en Contact Avec Nous',
    contactIntro: 'Nous nous engageons à offrir la meilleure expérience d\'assistance',
    contactAvailable: 'Support Disponible 24/7',
    responseTime: 'Temps de réaction en une heure',
    email: 'Email',
    whatsapp: 'WhatsApp',
    telegram: 'Telegram',
    faqSection: 'Questions Fréquemment Posées',
    trustPrivacy: 'Confiance et Confidentialité',
    contactUs: 'Nous Contacter',
    
    // FAQ Page
    frequentlyAskedQuestions: 'Questions Fréquemment Posées',
    
    // Footer
    premiumSubscriptions: 'Abonnements premium à une valeur exceptionnelle. Produits authentiques, support dédié et tranquillité d\'esprit totale.',
    categories: 'Catégories',
    quickLinks: 'Liens Rapides',
    support: 'Support',
    allRightsReserved: 'Tous Droits Réservés',
    securePayments: 'Paiements Sécurisés',
    sslEncrypted: 'Chiffré SSL',
  },
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    // Get language from localStorage or default to 'en'
    return localStorage.getItem('language') || 'en';
  });

  // Save language preference to localStorage
  useEffect(() => {
    localStorage.setItem('language', language);
  }, [language]);

  const t = (key) => {
    return translations[language]?.[key] || translations['en']?.[key] || key;
  };

  const changeLanguage = (lang) => {
    if (['en', 'fr'].includes(lang)) {
      setLanguage(lang);
    }
  };

  return (
    <LanguageContext.Provider value={{ language, t, changeLanguage }}>
      {children}
    </LanguageContext.Provider>
  );
};
